import { captureError } from "@/common/helpers/sentryHelper";
import { useMessage } from "@/common/messages";
import { AcsClientType } from "@/common/constants/shared";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useWebchatCallRequestStore } from "@/common/stores/nonpersistingStores/webchatCallRequest";
import { useWebchatCustomerClosedStore } from "@/common/stores/nonpersistingStores/webchatCustomerClosed";
import { InteractionType } from "@/common/types";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { PopoverView } from "@/components/atoms/popover";
import { Flex, GapSize } from "@/components/atoms/flex";
import { useIsAicLockedForInteraction } from "@/hooks/aicLocking";
import { useVoiceOutbound } from "@/hooks/outbound/voice";
import { useTextMonitoring } from "@/hooks/supervisor";
import {
	cancelWebchatVideoCallRequest,
	createWebchatVideoCallRequest,
} from "@/repository/queryFns/webchat";
import {
	ChatVideo20Regular,
	HourglassHalf20Regular,
} from "@fluentui/react-icons";
import {
	Button,
	Dropdown,
	Option,
	Text,
	makeStyles,
	tokens,
} from "@fluentui/react-components";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

const VIDEO_CALL_PENDING_MESSAGE_ID = "Interaction.Buttons.VideoCallPending";

const resolveVideoCallTooltipId = (
	hasCustomerLeftChat: boolean,
	isRequestPending: boolean
): string => {
	if (hasCustomerLeftChat) {
		return "Interaction.Messages.WebchatCustomerClosed";
	}

	if (isRequestPending) {
		return VIDEO_CALL_PENDING_MESSAGE_ID;
	}

	return "Interaction.Buttons.StartVideoCall";
};

const useStyles = makeStyles({
	container: {
		minWidth: "16rem",
		width: "18rem",
		padding: `${tokens.spacingVerticalL} 16px`,
	},
	title: {
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightSemibold,
		marginBottom: "8px",
	},
	dropdown: {
		width: "100%",
		marginBottom: "16px",
	},
	waitingText: {
		marginBottom: "16px",
	},
	cancelButton: {
		backgroundColor: tokens.colorPaletteRedBackground3,
		color: tokens.colorNeutralForegroundOnBrand,
		":hover": {
			backgroundColor: tokens.colorPaletteRedForeground1,
			color: tokens.colorNeutralForegroundOnBrand,
		},
		":hover:active": {
			backgroundColor: tokens.colorPaletteRedForeground1,
			color: tokens.colorNeutralForegroundOnBrand,
		},
	},
});

export const WebchatVideoCallButton = () => {
	const intl = useIntl();
	const classes = useStyles();
	const { show: showMessage } = useMessage();
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);
	const isTextMonitoring = useTextMonitoring();
	const isAicLocked = useIsAicLockedForInteraction(
		currentInteraction?.firstAgentAssignedAt
	);
	const { isOutboundAllowed, isOutboundPossible, trustedEndpoints } =
		useVoiceOutbound();
	const pendingRequest = useWebchatCallRequestStore((state) =>
		currentInteraction
			? state.pendingByConversationId[currentInteraction.id]
			: undefined
	);
	// The customer has to be on the other end to receive the invite; once they have left the chat the
	// agent still owns the interaction, so the button stays put but must not be clickable.
	const hasCustomerLeftChat = useWebchatCustomerClosedStore((state) =>
		currentInteraction
			? state.closedByConversationId[currentInteraction.id] === true
			: false
	);
	const setPending = useWebchatCallRequestStore((state) => state.setPending);
	const clearPending = useWebchatCallRequestStore(
		(state) => state.clearPending
	);

	const conversationId = currentInteraction?.id;

	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [selectedObjectId, setSelectedObjectId] = useState<string>();

	// The calling resource account is chosen by the agent at call time; only
	// Unify/ACS resource accounts (those with an ACS resource) can place the call.
	// trustedEndpoints repeats the same resource account once per outbound-right queue,
	// so dedupe by objectId to avoid duplicate dropdown entries (and colliding React keys).
	const acsEndpoints = useMemo(() => {
		const seenObjectIds = new Set<string>();

		return trustedEndpoints.filter((endpoint) => {
			if (
				!endpoint.acsResourceId ||
				!endpoint.objectId ||
				seenObjectIds.has(endpoint.objectId)
			) {
				return false;
			}

			seenObjectIds.add(endpoint.objectId);

			return true;
		});
	}, [trustedEndpoints]);

	// default to the single resource account when there is only one
	useEffect(() => {
		if (acsEndpoints.length === 1) {
			setSelectedObjectId(acsEndpoints[0]?.objectId);
		}
	}, [acsEndpoints]);

	// safety net: drop the pending state locally when it expires and the
	// Expired notification never arrives
	useEffect(() => {
		if (!pendingRequest || !conversationId) {
			return undefined;
		}

		const remainingMs = new Date(pendingRequest.expiresAt).getTime() - Date.now();
		const timer = setTimeout(
			() => clearPending(conversationId),
			Math.max(remainingMs, 0) + 5000
		);

		return () => clearTimeout(timer);
	}, [pendingRequest, conversationId, clearPending]);

	const createRequestMutation = useMutation({
		mutationFn: (variables: {
			conversationId: string;
			applicationInstanceId: string;
		}) =>
			createWebchatVideoCallRequest(
				variables.conversationId,
				variables.applicationInstanceId
			),
		onSuccess: (response, variables) => {
			setPending(
				variables.conversationId,
				response.requestId,
				response.expiresAt
			);
		},
		onError: (err: any, variables) => {
			captureError(err, "Error creating webchat video call request", undefined, {
				conversationId: variables.conversationId,
			});
			showMessage("workspace", {
				intent: "error",
				text: intl.formatMessage({
					id: "Interaction.Messages.WebchatCallRequestFailed",
				}),
			});
		},
	});

	const cancelRequestMutation = useMutation({
		mutationFn: (variables: { conversationId: string; requestId: string }) =>
			cancelWebchatVideoCallRequest(
				variables.conversationId,
				variables.requestId
			),
		onSuccess: (_response, variables) => {
			clearPending(variables.conversationId);
		},
		onError: (err: any, variables) => {
			// Leave the pending state in place — the request may already have been accepted;
			// the Expired/Ended notification (or the safety-net timer) will clear it.
			captureError(err, "Error cancelling webchat video call request", undefined, {
				conversationId: variables.conversationId,
			});
		},
	});

	const isClientSideAcsCallingPossible =
		!!appSettings.acsClientType &&
		!!appSettings.acsResourceEndpointUrl &&
		appSettings.acsClientType !== AcsClientType.Teams;

	// Keyed on the agent's own outbound access — outbound rights plus at least one ACS resource
	// account on a queue they have outbound right on (only ACS accounts can place this call, so an
	// agent whose outbound endpoints are all non-ACS gets no button rather than a dead one) — and on
	// the entry point's calling setting via agentCallingEnabled. Of these the server re-checks the
	// ownership, the entry point setting and the outbound rights; the endpoint part is client-side
	// only. Deliberately NOT keyed on the INTERACTION's queue having outbound right: `queues` holds
	// only the queues this agent is a member of, so after a transfer to an agent who isn't in the
	// originating queue that lookup finds nothing and reads as "no right", hiding the button on an
	// interaction they fully own. The call is placed from a resource account they pick from their
	// own endpoints, so the originating queue has no say in it.
	const isVisible =
		currentInteraction?.type === InteractionType.Webchat &&
		currentInteraction.agentCallingEnabled &&
		!isTextMonitoring &&
		isOutboundPossible &&
		acsEndpoints.length > 0 &&
		isClientSideAcsCallingPossible;

	if (!isVisible) {
		return null;
	}

	// Once the customer has accepted, the call is being placed (connecting) and can no longer be
	// cancelled — show a plain hourglass with no popover.
	if (pendingRequest?.accepted && conversationId) {
		return (
			<SquareToggleButton
				icon={<HourglassHalf20Regular />}
				tooltip={intl.formatMessage({
					id: "Interaction.Buttons.VideoCallConnecting",
				})}
				disabled
			/>
		);
	}

	// While waiting for the customer to respond, the hourglass opens a popover to cancel the invite.
	if (pendingRequest && conversationId) {
		const cancelCall = () => {
			cancelRequestMutation.mutate({
				conversationId,
				requestId: pendingRequest.requestId,
			});
			setIsPopoverOpen(false);
		};

		return (
			<PopoverView
				open={isPopoverOpen}
				disableAutoFocus
				positioning={{ position: "below" }}
				trigger={
					<SquareToggleButton
						icon={<HourglassHalf20Regular />}
						tooltip={intl.formatMessage({
							id: VIDEO_CALL_PENDING_MESSAGE_ID,
						})}
						onClick={() => setIsPopoverOpen((prev) => !prev)}
					/>
				}
				content={
					<Flex column className={classes.container}>
						<Text className={classes.waitingText}>
							{intl.formatMessage({
								id: VIDEO_CALL_PENDING_MESSAGE_ID,
							})}
						</Text>
						<Flex hAlign="flex-end">
							<Button
								appearance="primary"
								className={classes.cancelButton}
								disabled={cancelRequestMutation.isPending}
								onClick={cancelCall}
							>
								{intl.formatMessage({
									id: "Interaction.Buttons.CancelVideoCall",
								})}
							</Button>
						</Flex>
					</Flex>
				}
			/>
		);
	}

	const isRequestPending = !!pendingRequest || createRequestMutation.isPending;
	const isDisabled =
		isRequestPending ||
		isAicLocked ||
		!isOutboundAllowed ||
		currentInteraction.isOnHold ||
		hasCustomerLeftChat;

	// show the phone number alongside the name when the resource account has one
	const endpointLabel = (endpoint: { name: string; phoneNumber: string }) =>
		endpoint.phoneNumber
			? `${endpoint.name} (${endpoint.phoneNumber})`
			: endpoint.name;

	const selectedEndpoint = acsEndpoints.find(
		(endpoint) => endpoint.objectId === selectedObjectId
	);
	const selectedEndpointLabel = selectedEndpoint
		? endpointLabel(selectedEndpoint)
		: "";

	const startCall = () => {
		if (!selectedEndpoint || !conversationId) {
			return;
		}

		createRequestMutation.mutate({
			conversationId,
			applicationInstanceId: selectedEndpoint.objectId,
		});
		setIsPopoverOpen(false);
	};

	return (
		<PopoverView
			open={isPopoverOpen}
			disableAutoFocus
			positioning={{ position: "below" }}
			trigger={
				<SquareToggleButton
					icon={<ChatVideo20Regular />}
					tooltip={intl.formatMessage({
						id: resolveVideoCallTooltipId(hasCustomerLeftChat, isRequestPending),
					})}
					disabled={isDisabled}
					onClick={() => setIsPopoverOpen((prev) => !prev)}
				/>
			}
			content={
				<Flex column className={classes.container}>
					<Text className={classes.title}>
						{intl.formatMessage({
							id: "Interaction.Buttons.VoiceResourceAccount",
						})}
					</Text>
					<Dropdown
						className={classes.dropdown}
						// Render the listbox inside the popover; without this the
						// dropdown popup portals outside it, so the first click is
						// swallowed (opens only via arrow key) and Start then needs a
						// second click. Matches the IVR transfer combobox pattern.
						inlinePopup
						placeholder={intl.formatMessage({
							id: "Interaction.Buttons.SelectVoiceResourceAccount",
						})}
						value={selectedEndpointLabel}
						selectedOptions={
							selectedObjectId ? [selectedObjectId] : []
						}
						onOptionSelect={(_, data) =>
							setSelectedObjectId(data.optionValue)
						}
					>
						{acsEndpoints.map((endpoint) => (
							<Option
								key={endpoint.objectId}
								value={endpoint.objectId}
								text={endpointLabel(endpoint)}
							>
								{endpointLabel(endpoint)}
							</Option>
						))}
					</Dropdown>
					<Flex gap={GapSize.Small} hAlign="flex-end">
						<Button onClick={() => setIsPopoverOpen(false)}>
							{intl.formatMessage({ id: "Common.Cancel" })}
						</Button>
						<Button
							appearance="primary"
							disabled={!selectedEndpoint || isDisabled}
							onClick={startCall}
						>
							{intl.formatMessage({
								id: "Interaction.Buttons.StartCall",
							})}
						</Button>
					</Flex>
				</Flex>
			}
		/>
	);
};
